import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";

import { ArrowRight, Mail, Menu, Phone, X } from "lucide-react";

import SectionHeader from "@/components/SectionHeader";
import SchedulerEmbed from "@/components/SchedulerEmbed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState({
    consultation: false,
    newsletter: false,
    contact: false,
  });
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (e) {
      console.error(e, "Error loading landing page content");
      toast.error("Unable to load the site right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateContactField = (event) => {
    const { name, value } = event.target;
    setContactForm((current) => ({ ...current, [name]: value }));
  };

  const handleConsultationSubmit = async (payload) => {
    setSubmitting((current) => ({ ...current, consultation: true }));

    try {
      const response = await axios.post(`${API}/consultations`, payload);
      toast.success(response.data.message);
      return true;
    } catch (error) {
      console.error(error, "Consultation request failed");
      toast.error("We couldn't send your consultation request.");
      return false;
    } finally {
      setSubmitting((current) => ({ ...current, consultation: false }));
    }
  };

  const handleNewsletterSubmit = async (event) => {
    event.preventDefault();
    setSubmitting((current) => ({ ...current, newsletter: true }));

    try {
      const response = await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success(response.data.message);
      setNewsletterEmail("");
    } catch (error) {
      console.error(error, "Newsletter signup failed");
      toast.error("We couldn't add that email just yet.");
    } finally {
      setSubmitting((current) => ({ ...current, newsletter: false }));
    }
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setSubmitting((current) => ({ ...current, contact: true }));

    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      toast.success(response.data.message);
      setContactForm({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      console.error(error, "Contact request failed");
      toast.error("We couldn't send your message right now.");
    } finally {
      setSubmitting((current) => ({ ...current, contact: false }));
    }
  };

  if (loading || !content) {
    return (
      <div className="min-h-screen bg-[#F8F8F6] px-6 py-16 text-[#0A0A0A] md:px-12 lg:px-24" data-testid="landing-page-loading-state">
        <div className="mx-auto max-w-[1400px] space-y-8">
          <div className="h-5 w-40 animate-pulse bg-black/10" />
          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="space-y-4">
              <div className="h-6 w-32 animate-pulse bg-black/10" />
              <div className="h-20 w-full max-w-3xl animate-pulse bg-black/10" />
              <div className="h-20 w-full max-w-2xl animate-pulse bg-black/10" />
            </div>
            <div className="aspect-[4/4.5] animate-pulse border border-black/10 bg-black/5" />
          </div>
        </div>
      </div>
    );
  }

  const { brand, hero, metrics, services, about, approach, scheduler } = content;
  const hasPhone = Boolean(brand.contact_phone);
  const shortFirstName = brand.name.split(" ")[0];
  const navigationItems = [
    ["About", "about"],
    ["Services", "services"],
    ["Approach", "approach"],
    ["Schedule", "scheduler"],
    ["Contact", "contact"],
  ];

  return (
    <div className="app-shell min-h-screen bg-[#F8F8F6] text-[#0A0A0A]" data-testid="consulting-landing-page">
      <Toaster position="top-right" />

      <header className="sticky top-0 z-50 border-b border-black/5 bg-white/80 backdrop-blur-xl" data-testid="site-header">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-6 py-4 md:px-12 lg:px-24">
          <div>
            <p className="text-lg font-black tracking-[-0.06em]" data-testid="brand-name">
              {brand.name}
            </p>
            <p className="text-xs uppercase tracking-[0.2em] text-[#52525B]" data-testid="brand-tagline">
              {brand.tagline}
            </p>
          </div>

          <nav className="hidden items-center gap-6 md:flex" data-testid="header-navigation">
            {navigationItems.map(([label, anchor]) => (
              <a
                key={anchor}
                href={`#${anchor}`}
                className="text-sm font-medium text-[#52525B] transition-colors hover:text-[#0A0A0A]"
                data-testid={`nav-link-${anchor}`}
              >
                {label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Button asChild className="h-auto rounded-none border border-[#0047FF] bg-[#0047FF] px-4 py-3 text-sm text-white hover:bg-[#0036CC] md:px-6" data-testid="header-book-button">
              <a href="#scheduler" onClick={() => setMobileMenuOpen(false)}>
                Book now
              </a>
            </Button>

            <button
              type="button"
              className="inline-flex h-11 w-11 items-center justify-center border border-black/10 bg-white text-[#0A0A0A] transition-colors hover:bg-[#0A0A0A] hover:text-white md:hidden"
              onClick={() => setMobileMenuOpen((current) => !current)}
              data-testid="mobile-menu-toggle-button"
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen ? (
          <div className="border-t border-black/10 bg-white px-6 py-5 md:hidden" data-testid="mobile-navigation-menu">
            <nav className="grid gap-3">
              {navigationItems.map(([label, anchor]) => (
                <a
                  key={anchor}
                  href={`#${anchor}`}
                  className="flex items-center justify-between border border-black/10 px-4 py-4 text-sm font-medium text-[#0A0A0A] transition-colors hover:bg-[#0A0A0A] hover:text-white"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${anchor}`}
                >
                  <span>{label}</span>
                  <ArrowRight className="h-4 w-4" />
                </a>
              ))}
            </nav>
          </div>
        ) : null}
      </header>

      <main>
        <section className="mx-auto max-w-[1400px] px-6 pb-16 pt-8 md:px-12 md:pb-20 lg:px-24 lg:pt-12" data-testid="hero-section">
          <div className="mb-8 inline-flex items-center gap-3 border border-black/10 bg-white px-4 py-3 text-sm text-[#52525B]" data-testid="hero-announcement-banner">
            <span className="h-2 w-2 rounded-full bg-[#0047FF]" />
            {hero.announcement}
          </div>

          <div className="grid items-start gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="space-y-8">
              <p className="eyebrow" data-testid="hero-eyebrow">
                {hero.eyebrow}
              </p>
              <div className="space-y-6">
                <h1 className="max-w-4xl text-5xl font-black leading-[0.94] tracking-[-0.08em] text-[#0A0A0A] sm:text-6xl lg:text-7xl" data-testid="hero-title">
                  {hero.title}
                </h1>
                <p className="max-w-2xl text-base leading-8 text-[#52525B] sm:text-lg" data-testid="hero-description">
                  {hero.description}
                </p>
              </div>

              <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap" data-testid="hero-cta-group">
                <Button asChild className="h-auto rounded-none border border-[#0047FF] bg-[#0047FF] px-8 py-4 text-sm font-medium text-white hover:bg-[#0036CC]" data-testid="hero-book-button">
                  <a href="#scheduler">{hero.primary_cta}</a>
                </Button>
                <Button asChild variant="outline" className="h-auto rounded-none border border-[#0A0A0A] px-8 py-4 text-sm font-medium text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="hero-services-button">
                  <a href="#services">{hero.secondary_cta}</a>
                </Button>
                <Button asChild variant="ghost" className="h-auto rounded-none px-0 py-4 text-sm font-medium text-[#0A0A0A] hover:bg-transparent hover:text-[#0047FF]" data-testid="hero-approach-button">
                  <a href="#approach" className="inline-flex items-center gap-2">
                    {hero.tertiary_cta}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6" data-testid="metrics-strip">
                {metrics.map((metric) => (
                  <div key={metric.label} className="border border-black/10 bg-white p-4" data-testid={`metric-card-${metric.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <p className="text-[2rem] font-black leading-none tracking-[-0.06em] text-[#0A0A0A] xl:text-[2.1rem]" data-testid={`metric-value-${metric.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {metric.value}
                    </p>
                    <p className="mt-3 text-sm leading-6 text-[#52525B]" data-testid={`metric-label-${metric.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {metric.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="hero-visual-panel" data-testid="hero-visual-panel">
              <div
                className="relative overflow-hidden border border-black/10 bg-white px-8 py-10 md:min-h-[640px]"
                data-testid="hero-minimal-panel"
              >
                <div
                  className="pointer-events-none absolute inset-0 opacity-60"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, rgba(10,10,10,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(10,10,10,0.05) 1px, transparent 1px)",
                    backgroundSize: "64px 64px",
                  }}
                />

                <div className="relative flex h-full flex-col justify-between gap-12">
                  <div className="space-y-10">
                    <div className="grid gap-3 sm:grid-cols-2" data-testid="hero-focus-grid">
                      {[
                        "Advisory engagements",
                        "Fractional leadership",
                        "Program recovery",
                        "Critical inflection points",
                      ].map((item) => (
                        <div
                          key={item}
                          className="border border-black/10 bg-white px-4 py-4 text-sm font-medium text-[#0A0A0A]"
                          data-testid={`hero-focus-item-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          {item}
                        </div>
                      ))}
                    </div>

                    <div className="max-w-lg border-t border-black/10 pt-8" data-testid="hero-approach-block">
                      <p className="eyebrow">Approach</p>
                      <p className="mt-4 text-2xl font-semibold leading-9 tracking-[-0.04em] text-[#0A0A0A]">
                        Structured thinking, calm delivery, and engineering rigor applied to complex programs.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 border-t border-black/10 pt-8 sm:grid-cols-2" data-testid="hero-contact-summary">
                    <div className="space-y-2">
                      <p className="eyebrow">Direct contact</p>
                      <p className="flex items-center gap-3 text-sm text-[#0A0A0A]" data-testid="hero-contact-email">
                        <Mail className="h-4 w-4" />
                        {brand.contact_email}
                      </p>
                      {hasPhone ? (
                        <p className="flex items-center gap-3 text-sm text-[#0A0A0A]" data-testid="hero-contact-phone">
                          <Phone className="h-4 w-4" />
                          {brand.contact_phone}
                        </p>
                      ) : null}
                    </div>

                    <div className="space-y-2">
                      <p className="eyebrow">Based in</p>
                      <p className="text-sm text-[#52525B]" data-testid="hero-contact-location">
                        {brand.location}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="scroll-mt-28 mx-auto max-w-[1400px] border-y border-black/10 px-6 py-24 md:px-12 lg:px-24" data-testid="about-section">
          <div className="grid gap-12 lg:grid-cols-[0.42fr_1fr]">
            <div className="space-y-6 lg:sticky lg:top-28 lg:self-start">
              <p className="eyebrow" data-testid="about-section-eyebrow">
                {about.title}
              </p>
              <h2 className="max-w-md text-3xl font-black tracking-[-0.05em] text-[#0A0A0A] sm:text-4xl lg:text-5xl" data-testid="about-section-title">
                A career shaped by firsts, field judgment, and engineering rigor.
              </h2>
              <p className="max-w-sm text-sm leading-7 text-[#52525B]" data-testid="about-section-supporting-text">
                From early RF engineering work to public-safety infrastructure and complex program leadership, the through-line has been fundamentals under pressure.
              </p>
            </div>

            <div className="grid gap-8" data-testid="about-section-content">
              {about.paragraphs.map((paragraph, index) => (
                <div
                  key={paragraph.slice(0, 40)}
                  className="max-w-4xl"
                  data-testid={`about-paragraph-${index + 1}`}
                >
                  <p className="max-w-4xl text-base leading-8 text-[#52525B]">{paragraph}</p>
                </div>
              ))}

              <p className="border-t border-black/10 pt-6 text-lg font-medium italic leading-8 text-[#0A0A0A]" data-testid="about-closing-line">
                {about.closing}
              </p>
            </div>
          </div>
        </section>

        <section id="services" className="scroll-mt-28 mx-auto max-w-[1400px] border-y border-black/10 px-6 py-20 md:px-12 lg:px-24" data-testid="services-section">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <SectionHeader
              eyebrow="What we do"
              title="Project leadership support for teams navigating pressure, ambiguity, and change."
              description="The work is practical, structured, and grounded in delivery reality — from standing up new programs to helping existing ones regain traction."
              testId="services-section-header"
            />

            <div className="grid gap-5 md:grid-cols-3">
              {services.map((service) => (
                <article key={service.title} className="hover-invert-card min-h-[240px]" data-testid={`service-card-${service.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="space-y-5 p-6">
                    <p className="eyebrow">Capability</p>
                    <h3 className="text-2xl font-bold tracking-[-0.04em]" data-testid={`service-title-${service.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {service.title}
                    </h3>
                    <p className="text-sm leading-7 text-current/80" data-testid={`service-description-${service.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {service.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="approach" className="scroll-mt-28 mx-auto max-w-[1400px] px-6 py-24 md:px-12 lg:px-24" data-testid="approach-section">
          <div className="grid gap-12 lg:grid-cols-[0.48fr_1fr]">
            <div className="space-y-5">
              <p className="eyebrow" data-testid="approach-section-eyebrow">
                {approach.title}
              </p>
              <h2 className="text-4xl font-black tracking-[-0.05em] text-[#0A0A0A] sm:text-5xl" data-testid="approach-section-title">
                Fundamentals are the structural integrity behind complex delivery.
              </h2>
            </div>

            <div className="border border-black/10 bg-white p-8 md:p-10" data-testid="approach-panel">
              <p className="max-w-3xl text-3xl font-semibold leading-10 tracking-[-0.04em] text-[#0A0A0A]" data-testid="approach-intro">
                {approach.intro}
              </p>

              <div className="mt-8 grid gap-6 border-t border-black/10 pt-8">
                {approach.paragraphs.map((paragraph, index) => (
                  <p
                    key={paragraph.slice(0, 40)}
                    className="max-w-4xl text-base leading-8 text-[#52525B]"
                    data-testid={`approach-paragraph-${index + 1}`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="scheduler" className="scroll-mt-28 mx-auto max-w-[1400px] px-6 py-24 md:px-12 lg:px-24" data-testid="scheduler-section">
          <div className="space-y-10">
            <SectionHeader
              eyebrow="Appointment calendar"
              title="A straightforward way to begin the conversation."
              description="Choose the topic, reserve a time, and share enough context to make the first conversation useful from minute one."
              testId="scheduler-section-header"
            />
            <SchedulerEmbed scheduler={scheduler} />
          </div>
        </section>

        <section id="case-studies" className="scroll-mt-28 mx-auto max-w-[1400px] px-6 py-24 md:px-12 lg:px-24" data-testid="case-studies-section">
          <div className="space-y-12">
            <SectionHeader
              eyebrow="Downloadable case studies"
              title="Downloadable case studies are forthcoming."
              description="This section is under construction and will be updated with selected case studies as they are finalized."
              testId="case-studies-section-header"
            />
            <div className="border border-black/10 bg-white px-8 py-16 text-center" data-testid="case-studies-placeholder-panel">
              <p className="eyebrow">Under construction</p>
              <p className="mx-auto mt-5 max-w-2xl text-2xl font-semibold tracking-[-0.04em] text-[#0A0A0A]" data-testid="case-studies-placeholder-title">
                Selected case studies will be added here soon.
              </p>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-[#52525B]" data-testid="case-studies-placeholder-description">
                In the meantime, the consultation form is the best place to request relevant examples or discuss comparable program work.
              </p>
            </div>
          </div>
        </section>

        <section id="insights" className="scroll-mt-28 mx-auto max-w-[1400px] border-y border-black/10 px-6 py-24 md:px-12 lg:px-24" data-testid="insights-section">
          <div className="space-y-12">
            <SectionHeader
              eyebrow="Blog previews"
              title="Blog previews are forthcoming."
              description="This section is under construction and will be updated with new articles and notes when they are ready."
              testId="insights-section-header"
            />
            <div className="border border-black/10 bg-white px-8 py-16 text-center" data-testid="blog-placeholder-panel">
              <p className="eyebrow">Forthcoming</p>
              <p className="mx-auto mt-5 max-w-2xl text-2xl font-semibold tracking-[-0.04em] text-[#0A0A0A]" data-testid="blog-placeholder-title">
                Articles and blog previews will appear here soon.
              </p>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-[#52525B]" data-testid="blog-placeholder-description">
                You can still subscribe below to be notified when new writing and project leadership notes are published.
              </p>
            </div>
          </div>
        </section>

        <section id="contact" className="scroll-mt-28 mx-auto max-w-[1400px] px-6 py-24 md:px-12 lg:px-24" data-testid="contact-section">
          <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="border border-black/10 bg-[#0A0A0A] p-8 text-white" data-testid="newsletter-panel">
              <p className="eyebrow !text-white/70">Newsletter</p>
              <h2 className="mt-6 max-w-lg text-4xl font-black tracking-[-0.05em] text-white sm:text-5xl" data-testid="newsletter-title">
                Stay close to thoughtful notes on project and program leadership.
              </h2>
              <p className="mt-5 max-w-md text-sm leading-7 text-white/75" data-testid="newsletter-description">
                Get low-noise updates on standing up programs, course correction, and the practical craft of leadership under pressure.
              </p>

              <form className="mt-8 flex flex-col gap-4 sm:flex-row" onSubmit={handleNewsletterSubmit} data-testid="newsletter-form">
                <Input
                  type="email"
                  value={newsletterEmail}
                  onChange={(event) => setNewsletterEmail(event.target.value)}
                  placeholder="Your work email"
                  className="h-12 rounded-none border-white/20 bg-white/5 px-4 text-white placeholder:text-white/45 focus-visible:ring-0"
                  data-testid="newsletter-email-input"
                  required
                />
                <Button
                  type="submit"
                  disabled={submitting.newsletter}
                  className="h-12 rounded-none border border-white bg-white px-6 text-sm font-medium text-[#0A0A0A] hover:bg-transparent hover:text-white"
                  data-testid="newsletter-submit-button"
                >
                  {submitting.newsletter ? "Joining..." : "Subscribe"}
                </Button>
              </form>
            </div>

            <div className="border border-black/10 bg-white p-8" data-testid="contact-form-panel">
              <p className="eyebrow">Get in touch</p>
              <div className={`mt-6 grid gap-5 ${hasPhone ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-[#52525B]">Email</p>
                  <p className="mt-2 text-lg font-semibold text-[#0A0A0A]" data-testid="contact-email-display">
                    {brand.contact_email}
                  </p>
                </div>
                {hasPhone ? (
                  <div>
                    <p className="text-sm uppercase tracking-[0.2em] text-[#52525B]">Phone</p>
                    <p className="mt-2 text-lg font-semibold text-[#0A0A0A]" data-testid="contact-phone-display">
                      {brand.contact_phone}
                    </p>
                  </div>
                ) : null}
              </div>

              <form className="mt-8 grid gap-5" onSubmit={handleContactSubmit} data-testid="contact-form">
                <div className="grid gap-5 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="scheduler-label" htmlFor="contact-name">
                      Name
                    </label>
                    <Input
                      id="contact-name"
                      name="name"
                      value={contactForm.name}
                      onChange={updateContactField}
                      placeholder="Jordan Lee"
                      className="scheduler-input"
                      data-testid="contact-name-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="scheduler-label" htmlFor="contact-email">
                      Work email
                    </label>
                    <Input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={updateContactField}
                      placeholder="jordan@company.com"
                      className="scheduler-input"
                      data-testid="contact-email-input"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="scheduler-label" htmlFor="contact-company">
                    Company
                  </label>
                  <Input
                    id="contact-company"
                    name="company"
                    value={contactForm.company}
                    onChange={updateContactField}
                    placeholder="Atlas Ventures"
                    className="scheduler-input"
                    data-testid="contact-company-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="scheduler-label" htmlFor="contact-message">
                    What can we help with?
                  </label>
                  <Textarea
                    id="contact-message"
                    name="message"
                    value={contactForm.message}
                    onChange={updateContactField}
                    placeholder="Share the challenge, timeline, or type of advisory support you want."
                    className="scheduler-input min-h-[148px] rounded-none border-x-0 border-t-0 px-0 py-3 shadow-none"
                    data-testid="contact-message-input"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting.contact}
                  className="h-auto self-start rounded-none border border-[#0047FF] bg-[#0047FF] px-8 py-4 text-sm font-medium text-white hover:bg-[#0036CC]"
                  data-testid="contact-submit-button"
                >
                  {submitting.contact ? "Sending message..." : `Contact ${shortFirstName}`}
                </Button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-black/10 bg-white" data-testid="site-footer">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-5 px-6 py-8 text-sm text-[#52525B] md:px-12 lg:flex-row lg:items-center lg:justify-between lg:px-24">
          <p data-testid="footer-brand-line">{brand.name} — {brand.tagline}.</p>
          <div className="flex flex-wrap items-center gap-5 text-[#0A0A0A]">
            <a href="#about" className="transition-colors hover:text-[#0047FF]" data-testid="footer-about-link">
              About
            </a>
            <a href="#approach" className="transition-colors hover:text-[#0047FF]" data-testid="footer-approach-link">
              Approach
            </a>
            <a href="#scheduler" className="transition-colors hover:text-[#0047FF]" data-testid="footer-schedule-link">
              Book consultation
            </a>
            <a href="#contact" className="transition-colors hover:text-[#0047FF]" data-testid="footer-contact-link">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
